import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase';
import { scheduleService } from '../../services/scheduleService';
import '../../styles/GameTeamEditor.css';

const GameTeamEditor = ({ game, isOpen, onClose, onSave, seasonId, teams }) => {
  const [team1Id, setTeam1Id] = useState('');
  const [team2Id, setTeam2Id] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (game) {
      setTeam1Id(game.team1_id || '');
      setTeam2Id(game.team2_id || '');
    }
    setError(null);
  }, [game, isOpen]);

  const handleSave = async () => {
    if (!team1Id || !team2Id) {
      setError('Please select both teams');
      return;
    }

    if (team1Id === team2Id) {
      setError('Both teams cannot be the same');
      return;
    }

    setLoading(true);
    try {
      await scheduleService.updateGameTeams(game.id, parseInt(team1Id), parseInt(team2Id));
      setError(null);
      onSave();
      onClose();
    } catch (err) {
      setError('Failed to update teams: ' + (err.message || 'Unknown error'));
      console.error('Error updating game teams:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !game) return null;

  const availableTeams = teams || [];
  const team1Name = availableTeams.find(t => t.id === parseInt(team1Id))?.name || 'Select Team';
  const team2Name = availableTeams.find(t => t.id === parseInt(team2Id))?.name || 'Select Team';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content game-team-editor" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Change Teams</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="game-info">
            <p><strong>Week {game.week}</strong></p>
            <p className="current-matchup">{game.team1?.name} vs {game.team2?.name}</p>
          </div>

          <div className="teams-selector">
            <div className="team-selector-group">
              <label htmlFor="team1-select">Team 1</label>
              <select
                id="team1-select"
                value={team1Id}
                onChange={(e) => setTeam1Id(e.target.value)}
                className="team-select"
                disabled={loading}
              >
                <option value="">Select Team 1</option>
                {availableTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.abbreviation})
                  </option>
                ))}
              </select>
              {team1Id && (
                <div className="team-preview">
                  {availableTeams.find(t => t.id === parseInt(team1Id))?.logo_url && (
                    <img 
                      src={availableTeams.find(t => t.id === parseInt(team1Id))?.logo_url} 
                      alt={team1Name}
                      className="team-logo-small"
                    />
                  )}
                  <span>{team1Name}</span>
                </div>
              )}
            </div>

            <div className="vs-divider">vs</div>

            <div className="team-selector-group">
              <label htmlFor="team2-select">Team 2</label>
              <select
                id="team2-select"
                value={team2Id}
                onChange={(e) => setTeam2Id(e.target.value)}
                className="team-select"
                disabled={loading}
              >
                <option value="">Select Team 2</option>
                {availableTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.abbreviation})
                  </option>
                ))}
              </select>
              {team2Id && (
                <div className="team-preview">
                  {availableTeams.find(t => t.id === parseInt(team2Id))?.logo_url && (
                    <img 
                      src={availableTeams.find(t => t.id === parseInt(team2Id))?.logo_url} 
                      alt={team2Name}
                      className="team-logo-small"
                    />
                  )}
                  <span>{team2Name}</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={handleSave}
            disabled={loading || !team1Id || !team2Id}
          >
            {loading ? 'Updating...' : 'Update Teams'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameTeamEditor;
